"use client";

import { useEffect, useRef, useState } from "react";

const DURATION_MS = 1400;

// Counts a formatted amount like "€1.200" up from zero once it scrolls into view.
// The server renders the final value, so no-JS, crawlers and reduced motion all
// see the real number. An invisible copy reserves the final width so the
// centered text does not shift while the digits grow.
export function CountUp({ value }: { value: string }) {
  const match = value.match(/^(\D*)(\d[\d.,]*)(\D*)$/);
  const target = match ? Number(match[2].replace(/\D/g, "")) : 0;
  const separator = match?.[2].match(/\D/)?.[0] ?? "";
  const ref = useRef<HTMLSpanElement>(null);
  const [current, setCurrent] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !match || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    setCurrent(0);
    let frame = 0;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        io.disconnect();
        const start = performance.now();
        const tick = (now: number) => {
          const t = Math.min((now - start) / DURATION_MS, 1);
          setCurrent(Math.round(target * (1 - (1 - t) ** 3)));
          if (t < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.6 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!match || current === null) return <span ref={ref}>{value}</span>;

  const digits = String(current).replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return (
    <span ref={ref} className="relative inline-block">
      <span className="sr-only">{value}</span>
      <span aria-hidden className="invisible">{value}</span>
      <span aria-hidden className="absolute inset-0">
        {match[1]}
        {digits}
        {match[3]}
      </span>
    </span>
  );
}
